/**
 * Is a turn running in this conversation RIGHT NOW, when the client didn't start it?
 *
 * The harness stamps `pendingPredictionId` on the conversation for the duration of every gateway
 * iteration and clears it when the turn settles, republishing the entity on the CONVERSATION
 * duplexer channel each time. That makes it the one signal a client can read for a turn it did not
 * initiate — a pack accept, a pulse, a brief, an agent report. Until this existed, every client
 * derived "busy" purely from its OWN send, so a server-started turn rendered as a dead screen: the
 * pack-accept report on 2026-08-07 was ~15s of silence followed by a push, which read as a failure.
 *
 * Shared 1:1 with iOS `TurnActivity.swift` and Android `TurnActivity.kt`; the three test tables are
 * the same cases. Change the rule here and change it there.
 */

/**
 * How long a stamped `pendingPredictionId` is believed without a fresh bump.
 *
 * The stamp is cleared by the turn that set it, so a turn that dies uncleanly (pod restart, a
 * gateway callback that never lands) leaves it set forever. An indicator with no end is worse than
 * no indicator — it tells the user something is coming that never will — so the signal expires.
 * Every iteration rewrites the conversation and bumps `updatedDate`, and iterations are seconds
 * apart, so this only has to outlast the slowest single iteration, not the whole turn.
 */
export const TURN_STALE_AFTER_MS = 5 * 60 * 1000;

export interface TurnActivitySignal {
  /** The conversation's `pendingPredictionId` — null/empty when no turn is in flight. */
  pendingPredictionId?: string | null;
  /** The conversation's `updatedDate`; an unparseable or absent value reads as stale. */
  updatedDate?: string | Date | null;
}

/**
 * Should a freshly-arrived CONVERSATION frame be applied, given the newest one already applied?
 *
 * The duplexer does NOT guarantee ordering, and the harness publishes the conversation entity on every
 * write — including writes that carry NO stamp while a turn is live. Two such frames were observed 0.5s
 * apart in production (2026-08-07): `updatedDate 12:15:21.956` with a null `pendingPredictionId`, then
 * `12:15:22.303` carrying the id. Applying the older one last clears the indicator mid-turn.
 *
 * So a frame is applied only when it is at least as new as the last applied one. Equal timestamps pass:
 * two writes can share a millisecond, and dropping the second could lose a real clear.
 */
export function shouldApplyFrame(frameUpdatedAt: number | null, lastAppliedAt: number | null): boolean {
  if (frameUpdatedAt === null) {
    return false;
  }
  return lastAppliedAt === null || frameUpdatedAt >= lastAppliedAt;
}

/** Milliseconds for a signal's `updatedDate`, or null when absent/unparseable. */
export function signalUpdatedAt(signal: TurnActivitySignal | null | undefined): number | null {
  return toMillis(signal?.updatedDate);
}

/** True when the conversation carries a live, non-expired pending turn. */
export function turnActive(signal: TurnActivitySignal | null | undefined, now: number = Date.now()): boolean {
  if (!signal?.pendingPredictionId) {
    return false;
  }
  const updatedMs = toMillis(signal.updatedDate);
  if (updatedMs === null) {
    return false;
  }
  // A clock-skewed future timestamp is still a live turn — only the past can go stale.
  return now - updatedMs < TURN_STALE_AFTER_MS;
}

function toMillis(value: string | Date | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Conversation sources that are a BACKGROUND run — work the user is not sitting in.
 *
 * `BRAIN` is a sub-agent's run, `PULSE` a routine's, `BRIEF` a brief round-table's, and `SYSTEM` a
 * parentless build. None of them is "the Maestro working in my chat", and counting them would light
 * the indicator for work the user cannot open.
 */
const BACKGROUND_CONVERSATION_SOURCES = new Set(['BRAIN', 'PULSE', 'BRIEF', 'SYSTEM']);

/**
 * Does a CONVERSATION frame belong to a conversation the USER converses in?
 *
 * **This is a DENY list, and that is the whole point.** It used to be an allow list of exactly one
 * value, `SYMPHONY`, written from the belief that "a SCHEDULER conversation is an outreach delivery".
 * That belief was wrong: `SCHEDULER` is what every Orion↔user chat is, on every channel — web, app,
 * WhatsApp, SMS — and it is the source `/api/conversations` itself queries for the sidebar. Nothing in
 * the product has ever CREATED a `SYMPHONY` conversation. So the allow list matched nothing that
 * exists, the indicator never lit for anybody, and no test failed, because the tests were written from
 * the same belief (they asserted that a SCHEDULER frame must NOT light it).
 *
 * An allow list fails DARK and fails SILENTLY: a source nobody listed, or a source added later, simply
 * stops the indicator for every user, and there is no error anywhere to notice. A deny list fails the
 * other way — an unknown source lights a pill that says something is happening. That is the cheaper
 * mistake by a wide margin, and it is the same reasoning that already made an ABSENT source trusted.
 *
 * Shared 1:1 with iOS `SymphonyStore.isUserFacingConversationSource` and Android's twin; the three test
 * tables are the same cases. Change the rule here and change it there.
 */
export function isUserFacingConversationSource(source: string | null | undefined): boolean {
  const normalized = (source ?? '').trim().toUpperCase();
  if (!normalized) {
    // Older frames omit it, and the alternative is an indicator that never lights.
    return true;
  }
  return !BACKGROUND_CONVERSATION_SOURCES.has(normalized);
}

/**
 * The user-facing sources a QUERY has to enumerate, because a query cannot express "not these".
 *
 * `isUserFacingConversationSource` is a deny list on purpose — an unknown source lights the indicator
 * rather than killing it. `/api/maestro-status` cannot do that: it asks the service for conversations
 * and must name the sources it wants, so it is an allow list, and an allow list is exactly what made
 * this feature dead for a month.
 *
 * Keeping it HERE, beside the predicate, is what stops the two drifting: `conversation-source.test.ts`
 * asserts every member passes the predicate AND that no known user-facing source is missing from this
 * list, so adding one to the product fails a test instead of going quietly unseeded.
 */
export const QUERYABLE_USER_FACING_SOURCES: readonly string[] = ['SCHEDULER', 'SYMPHONY'];
